import React, { useState } from 'react';
import './WorkOrderReportsTab.css';

// 아바타 색상 팔레트
const AVATAR_COLORS = [
  '#2196F3', '#9C27B0', '#FF9800', '#4CAF50', '#00BCD4',
  '#3F51B5', '#009688', '#FFC107', '#795548', '#607D8B'
];
const getAvatarColorInfo = (name = '') => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  hash = Math.abs(Math.imul(hash, 2654435761));
  const color = AVATAR_COLORS[hash % AVATAR_COLORS.length];
  const isDarkText = ['#FFC107', '#FF9800', '#00BCD4', '#4CAF50'].includes(color);
  return { bg: color, text: isDarkText ? 'dark' : 'light' };
};

const Avatar = ({ name = '', photo, size = 38 }) => {
  const [imgError, setImgError] = useState(false);
  const info = getAvatarColorInfo(name);
  const initial = name ? name.charAt(0) : '?';
  const showPhoto = photo && !imgError;

  return (
    <div
      className={`d-flex align-items-center justify-content-center text-${info.text} rounded-circle fw-bold shadow-sm flex-shrink-0 overflow-hidden`}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: showPhoto ? 'transparent' : info.bg,
        fontSize: `${size * 0.42}px`,
        border: '1px solid rgba(0,0,0,0.08)'
      }}
    >
      {showPhoto ? (
        <img
          src={photo}
          alt={name}
          onError={() => setImgError(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        initial
      )}
    </div>
  );
};

// 날짜 포맷 함수
const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return dateStr;
  }
};

function WorkOrderReportsTab({
  workOrder,
  onOpenReportForm,
  onOpenReportDetail,
  currentUser,
  isCc,
  isAssignee
}) {
  const [reportPage, setReportPage] = useState(1);
  const [selectedStatus, setSelectedStatus] = useState('all'); // 'all' | 'approved' | 'review' | 'rejected' | 'progress' | 'request'
  const [selectedAssigneeId, setSelectedAssigneeId] = useState(null);

  const STATUS_META = {
    all: { label: '전체', borderActive: '#0f172a', bgActive: '#1e293b', textActive: '#ffffff', badgeBg: '#1e293b' },
    approved: { label: '완료', borderActive: '#10b981', bgActive: '#dcfce7', textActive: '#065f46', badgeBg: '#10b981' },
    review: { label: '검토대기', borderActive: '#f59e0b', bgActive: '#fef3c7', textActive: '#92400e', badgeBg: '#f59e0b' },
    rejected: { label: '보완요청', borderActive: '#ef4444', bgActive: '#fee2e2', textActive: '#991b1b', badgeBg: '#ef4444' },
    progress: { label: '진행중', borderActive: '#3b82f6', bgActive: '#dbeafe', textActive: '#1e40af', badgeBg: '#3b82f6' },
    delay_progress: { label: '진행지연', borderActive: '#dc2626', bgActive: '#fee2e2', textActive: '#b91c1c', badgeBg: '#dc2626' },
    draft: { label: '임시저장', borderActive: '#64748b', bgActive: '#f1f5f9', textActive: '#334155', badgeBg: '#64748b' },
    request: { label: '접수대기', borderActive: '#8b5cf6', bgActive: '#ede9fe', textActive: '#5b21b6', badgeBg: '#8b5cf6' },
    delay_request: { label: '접수지연', borderActive: '#ef4444', bgActive: '#fee2e2', textActive: '#b91c1c', badgeBg: '#ef4444' },
  };

  const rawAssignees = workOrder?.assignees || [];
  const rawReports = workOrder?.reports || [];

  const currentUserIdStr = String(currentUser?.id || currentUser?.userid || '');
  const isAdmin = currentUser?.is_admin === true || currentUser?.is_admin === 1 || currentUser?.role === 'admin';
  const isAuthor = String(workOrder?.author_id || workOrder?.author?.id || '') === currentUserIdStr;

  // 참조자(Referrer) 판별: prop 우선 확인 후 ccs/referrers 배열 교차 검증
  const rawCcs = workOrder?.ccs || workOrder?.referrers || [];
  const isUserCc = Boolean(
    isCc === true ||
    (currentUserIdStr && rawCcs.some(c =>
      String(c.user_id) === currentUserIdStr ||
      String(c.user?.id) === currentUserIdStr ||
      String(c.user?.userid) === currentUserIdStr
    ))
  );

  // 임시저장(draft) 및 비공개(private) 보고서 열람 권한 필터링
  const visibleRawReports = rawReports.filter(rep => {
    const repUserId = String(rep.user_id || rep.reporter?.id || rep.reporter?.userid || '');
    if (rep.approval_status === 'draft') {
      return isAdmin || (currentUserIdStr && repUserId === currentUserIdStr);
    }
    const isPrivate = rep.is_public === false || rep.is_public === 0;
    if (isPrivate) {
      const isAuthor = String(workOrder?.author_id) === currentUserIdStr;
      const isMyRep = repUserId === currentUserIdStr;
      if (!isAdmin && !isAuthor && !isMyRep) return false;
    }
    return true;
  });

  // 1. 제출된 결과보고서 실데이터 가공
  const allReports = visibleRawReports.map(rep => {
    let statusKey = 'review';
    let statusLabel = '검토대기';
    let badgeBg = '#f59e0b';

    if (rep.approval_status === 'approved') {
      statusKey = 'approved';
      statusLabel = '완료';
      badgeBg = '#10b981';
    } else if (rep.approval_status === 'rejected') {
      statusKey = 'rejected';
      statusLabel = '보완요청';
      badgeBg = '#ef4444';
    } else if (rep.approval_status === 'submitted') {
      statusKey = 'review';
      statusLabel = '검토대기';
      badgeBg = '#f59e0b';
    } else if (rep.approval_status === 'draft') {
      statusKey = 'draft';
      statusLabel = '임시저장';
      badgeBg = '#64748b';
    }

    const reporterName = rep.reporter?.name || '작성자';
    const position = rep.reporter?.org?.position || '';
    const dept = rep.reporter?.org?.dept_name || '';

    // 해당 작성자의 총 보고서 수 판별 (2건 이상일 때만 차수 뱃지 노출)
    const authorReports = visibleRawReports.filter(r => {
      const uId = String(r.user_id || r.reporter?.id || r.reporter?.userid || '');
      const repUId = String(rep.user_id || rep.reporter?.id || rep.reporter?.userid || '');
      return uId === repUId;
    });

    return {
      id: rep.id,
      seq: rep.report_seq || 1,
      assigneeReportCount: authorReports.length,
      title: rep.report_title || '무제 처리결과',
      author: `${reporterName} ${position}`.trim(),
      authorName: reporterName,
      dept,
      date: formatDateTime(rep.submitted_at || rep.created_at),
      status: statusKey,
      statusLabel,
      badgeBg,
      files: Array.isArray(rep.files) ? rep.files.length : 0,
      userId: rep.user_id,
      assigneeId: rep.assignee_id,
      raw: rep
    };
  });

  // 현재 로그인한 사용자(담당자) 본인의 보고서 상태 체크 (참조자일 경우 담당자 배정 및 개인 보고서 매칭 원천 배제)
  const myAssignee = (!isUserCc && currentUserIdStr) ? rawAssignees.find(a => {
    const aId = String(a.user_id || a.user?.id || a.user?.userid || '');
    return aId && aId === currentUserIdStr;
  }) : null;

  const myReports = (!isUserCc && currentUserIdStr) ? rawReports.filter(r => {
    const rUserId = String(r.user_id || r.reporter?.id || r.reporter?.userid || '');
    return rUserId && rUserId === currentUserIdStr;
  }) : [];
  const mySubmittedReports = myReports.filter(r => r.approval_status !== 'draft' && r.approval_status !== 'temp');
  const submittedCount = mySubmittedReports.length;
  const myDraftReport = myReports.find(r => r.approval_status === 'draft' || r.approval_status === 'temp');
  const nextSeqText = submittedCount === 0 ? '' : `${submittedCount + 1}차 `;
  const latestSubmittedReport = mySubmittedReports.length > 0 ? mySubmittedReports[mySubmittedReports.length - 1] : null;
  const isReviewPending = latestSubmittedReport && (latestSubmittedReport.approval_status === 'submitted' || latestSubmittedReport.approval_status === 'review');
  const isRejectedPending = latestSubmittedReport && (latestSubmittedReport.approval_status === 'rejected' || latestSubmittedReport.approval_status === 'reject');

  const isWorkOrderActive = !['temp', 'cancel', 'canceled', 'stop', 'done', 'completed'].includes(workOrder?.status) && !workOrder?.deleted_at;
  const isAssigneeEligible = Boolean(myAssignee) && !isUserCc;
  const myProgress = Number(myAssignee?.progress_rate) || 0;
  // 타임라인과 100% 동일한 접수 완료 판정: pending/request가 아니며 progress/accepted/submitted/completed 상태이거나 진척도 > 0
  const isAssigneeAccepted = Boolean(
    myAssignee &&
    myAssignee.status !== 'pending' &&
    myAssignee.status !== 'request' &&
    (myAssignee.status === 'progress' || myAssignee.status === 'accepted' || myAssignee.status === 'in_progress' || myAssignee.status === 'submitted' || myAssignee.status === 'completed' || myProgress > 0)
  );
  const canWriteReport = isWorkOrderActive && isAssigneeEligible && isAssigneeAccepted;

  const now = new Date();

  // 2. 담당자 종합 진행 현황 실데이터 가공
  const allAssignees = rawAssignees.map(a => {
    const userReports = allReports.filter(r => r.userId === a.user_id || r.assigneeId === a.id);
    const latestReport = userReports[userReports.length - 1];

    // 개별 담당자 일정 지연 판별 (개별 마감일 a.end_date 우선, 없으면 업무지시 due_date, 타임라인과 100% 동일한 현재 시점 비교)
    const aDueStr = a.end_date || workOrder?.due_date;
    let isDelayed = false;
    if (aDueStr) {
      const dueTime = (typeof aDueStr === 'string' && aDueStr.length <= 10)
        ? new Date(aDueStr + 'T23:59:59')
        : new Date(aDueStr);
      isDelayed = dueTime < now;
    }

    const p = Number(a.progress_rate) || 0;
    const isAccepted = Boolean(
      a.status !== 'pending' &&
      a.status !== 'request' &&
      (a.status === 'progress' || a.status === 'accepted' || a.status === 'in_progress' || a.status === 'submitted' || a.status === 'completed' || p > 0)
    );

    let status = 'request';
    let statusText = '접수대기';
    let badgeBg = '#8b5cf6';
    let note = '접수 전';

    if (workOrder?.status === 'cancel') {
      status = 'cancel';
      statusText = '취소';
      badgeBg = '#ef4444';
      note = latestReport ? latestReport.title : '업무협조 취소됨';
    } else if (workOrder?.status === 'stop') {
      status = 'stop';
      statusText = '중단';
      badgeBg = '#64748b';
      note = latestReport ? latestReport.title : '요청자에 의해 업무 중단됨';
    } else if (a.status === 'completed' || a.status === 'done' || p === 100) {
      status = 'approved';
      statusText = '완료';
      badgeBg = '#10b981';
      note = latestReport ? latestReport.title : '완료됨';
    } else if (latestReport && latestReport.status === 'review') {
      status = 'review';
      statusText = '검토';
      badgeBg = '#f59e0b';
      note = latestReport.title;
    } else if (latestReport && latestReport.status === 'rejected') {
      status = 'rejected';
      statusText = '보완요청';
      badgeBg = '#ef4444';
      note = latestReport.title;
    } else if (latestReport && latestReport.status === 'draft') {
      if (isDelayed) {
        status = 'delay_progress';
        statusText = '진행지연';
        badgeBg = '#dc2626';
        note = '진행 지연 (임시저장)';
      } else {
        status = 'draft';
        statusText = '임시저장';
        badgeBg = '#64748b';
        note = latestReport.title;
      }
    } else {
      // 보고서 미제출 상태 (타임라인과 100% 동일한 판정)
      if (isDelayed) {
        if (!isAccepted) {
          status = 'delay_request';
          statusText = '접수지연';
          badgeBg = '#ef4444';
          note = '접수 지연 (미접수)';
        } else {
          status = 'delay_progress';
          statusText = '진행지연';
          badgeBg = '#dc2626';
          note = '진행 지연 (처리결과 미제출)';
        }
      } else {
        if (!isAccepted) {
          status = 'request';
          statusText = '접수대기';
          badgeBg = '#8b5cf6';
          note = '업무 미접수 (대기 중)';
        } else {
          status = 'progress';
          statusText = '진행중';
          badgeBg = '#3b82f6';
          note = '처리결과 미제출';
        }
      }
    }

    const userName = a.user?.name || a.name || '담당자';
    const pos = a.user?.org?.position || a.position || '';
    const dept = a.user?.org?.dept_name || a.dept_name || '';

    return {
      id: a.id,
      userId: a.user_id,
      name: userName,
      pos,
      dept,
      status,
      statusText,
      badgeBg,
      hasReport: !!latestReport,
      note
    };
  });

  // 필터 카운트 동적 계산
  const countAll = allAssignees.length;
  const countApproved = allAssignees.filter(a => a.status === 'approved').length;
  const countReview = allAssignees.filter(a => a.status === 'review').length;
  const countRejected = allAssignees.filter(a => a.status === 'rejected').length;
  const countProgress = allAssignees.filter(a => a.status === 'progress').length;
  const countDelayProgress = allAssignees.filter(a => a.status === 'delay_progress').length;
  const countDraft = allAssignees.filter(a => a.status === 'draft').length;
  const countRequest = allAssignees.filter(a => a.status === 'request').length;
  const countDelayRequest = allAssignees.filter(a => a.status === 'delay_request').length;

  // 선택된 담당자 정보
  const selectedAssignee = allAssignees.find(a => a.id === selectedAssigneeId);

  // 하단 결과보고서 목록 (상태 필터 + 담당자 선택 결합 연동)
  const filteredReports = allReports.filter(rep => {
    if (selectedStatus !== 'all' && rep.status !== selectedStatus) return false;
    if (selectedAssigneeId && rep.userId !== selectedAssignee?.userId && rep.assigneeId !== selectedAssignee?.id) return false;
    return true;
  })
  .sort((a, b) => {
    // 1️⃣ 1순위: 최종결과 최우선 및 차수 내림차순 (최종/4차 -> 3차 -> 2차 -> 1차)
    const isFinalA = Boolean(a.raw?.is_final || Number(a.raw?.progress_rate) === 100) ? 1 : 0;
    const isFinalB = Boolean(b.raw?.is_final || Number(b.raw?.progress_rate) === 100) ? 1 : 0;
    if (isFinalA !== isFinalB) return isFinalB - isFinalA;

    const seqA = Number(a.seq || 1);
    const seqB = Number(b.seq || 1);
    if (seqA !== seqB) return seqB - seqA;

    // 2️⃣ 2순위: 동일 차수 내에서 성명 오름차순 (가나다순)
    const nameA = String(a.authorName || '').trim();
    const nameB = String(b.authorName || '').trim();
    const nameCmp = nameA.localeCompare(nameB, 'ko');
    if (nameCmp !== 0) return nameCmp;

    // 3️⃣ 3순위: ID 내림차순 보조 정렬
    return (b.id || 0) - (a.id || 0);
  });

  const PAGE_SIZE = 4; // 페이지당 4개씩 페이징
  const totalPages = Math.max(1, Math.ceil(filteredReports.length / PAGE_SIZE));
  const startIndex = (reportPage - 1) * PAGE_SIZE;
  const currentReports = filteredReports.slice(startIndex, startIndex + PAGE_SIZE);

  return (
    <div className="wo-reports-container">

      {/* ── 1. 상단: 담당자 종합 진행 및 보고 현황 카드 그리드 ── */}
      <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <h6 className="fw-bold m-0 d-flex align-items-center gap-2" style={{ color: '#1e293b', fontSize: '1.05rem' }}>
              <i className="fas fa-users-cog text-primary" />
              담당자별 처리 진행현황
              <span className="badge rounded-pill bg-light text-dark border ms-1" style={{ fontSize: '0.75rem' }}>
                총 {countAll}명
              </span>
            </h6>
          </div>

          {/* 우측: 전체 원복(마스터) 및 상태 필터 버튼 */}
          <div className="d-flex align-items-center flex-wrap" style={{ gap: '6px' }}>
            {/* 1. 마스터 버튼: 전체 */}
            <button
              type="button"
              className="badge wo-filter-badge-btn px-2.5 py-1"
              style={selectedStatus === 'all'
                ? { backgroundColor: '#0f172a', color: '#ffffff', border: '1.5px solid #0f172a', fontWeight: 700 }
                : { backgroundColor: '#ffffff', color: '#334155', border: '1.5px solid #64748b', fontWeight: 600 }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedStatus('all');
                setSelectedAssigneeId(null);
                setReportPage(1);
              }}
              title="전체 보기 (초기화)"
            >
              {selectedStatus === 'all' && <i className="fas fa-check me-1" style={{ fontSize: '0.68rem' }} />}
              전체 {countAll}
            </button>

            {/* 마스터 버튼과 개별 상태 필터 사이 명확한 세로 구분선 */}
            <span style={{ width: '1.5px', height: '18px', backgroundColor: '#cbd5e1', margin: '0 6px', display: 'inline-block' }} />

            {/* 2. 개별 상태 필터 5종 */}
            <button
              type="button"
              className="badge wo-filter-badge-btn px-2 py-1"
              style={selectedStatus === 'approved'
                ? { backgroundColor: '#dcfce7', color: '#065f46', border: '1.5px solid #10b981', fontWeight: 700 }
                : { backgroundColor: '#ecfdf5', color: '#059669', border: '1px solid #a7f3d0', fontWeight: 600 }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedStatus(prev => (prev === 'approved' ? 'all' : 'approved'));
                setReportPage(1);
              }}
              title="완료 필터링"
            >
              {selectedStatus === 'approved' && <i className="fas fa-check me-1" style={{ fontSize: '0.68rem' }} />}
              완료 {countApproved}
            </button>
            <button
              type="button"
              className="badge wo-filter-badge-btn px-2 py-1"
              style={selectedStatus === 'review'
                ? { backgroundColor: '#fef3c7', color: '#92400e', border: '1.5px solid #f59e0b', fontWeight: 700 }
                : { backgroundColor: '#fffbeb', color: '#d97706', border: '1px solid #fde68a', fontWeight: 600 }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedStatus(prev => (prev === 'review' ? 'all' : 'review'));
                setReportPage(1);
              }}
              title="검토 대기 필터링"
            >
              {selectedStatus === 'review' && <i className="fas fa-check me-1" style={{ fontSize: '0.68rem' }} />}
              검토 {countReview}
            </button>
            <button
              type="button"
              className="badge wo-filter-badge-btn px-2 py-1"
              style={selectedStatus === 'rejected'
                ? { backgroundColor: '#fee2e2', color: '#991b1b', border: '1.5px solid #ef4444', fontWeight: 700 }
                : { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', fontWeight: 600 }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedStatus(prev => (prev === 'rejected' ? 'all' : 'rejected'));
                setReportPage(1);
              }}
              title="보완요청 필터링"
            >
              {selectedStatus === 'rejected' && <i className="fas fa-check me-1" style={{ fontSize: '0.68rem' }} />}
              보완요청 {countRejected}
            </button>
            <button
              type="button"
              className="badge wo-filter-badge-btn px-2 py-1"
              style={selectedStatus === 'progress'
                ? { backgroundColor: '#dbeafe', color: '#1e40af', border: '1.5px solid #3b82f6', fontWeight: 700 }
                : { backgroundColor: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', fontWeight: 600 }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedStatus(prev => (prev === 'progress' ? 'all' : 'progress'));
                setReportPage(1);
              }}
              title="진행중 필터링"
            >
              {selectedStatus === 'progress' && <i className="fas fa-check me-1" style={{ fontSize: '0.68rem' }} />}
              진행중 {countProgress}
            </button>
            <button
              type="button"
              className="badge wo-filter-badge-btn px-2 py-1"
              style={selectedStatus === 'delay_progress'
                ? { backgroundColor: '#fee2e2', color: '#b91c1c', border: '1.5px solid #dc2626', fontWeight: 700 }
                : { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', fontWeight: 600 }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedStatus(prev => (prev === 'delay_progress' ? 'all' : 'delay_progress'));
                setReportPage(1);
              }}
              title="진행지연 필터링"
            >
              {selectedStatus === 'delay_progress' && <i className="fas fa-check me-1" style={{ fontSize: '0.68rem' }} />}
              진행지연 {countDelayProgress}
            </button>
            <button
              type="button"
              className="badge wo-filter-badge-btn px-2 py-1"
              style={selectedStatus === 'draft'
                ? { backgroundColor: '#f1f5f9', color: '#334155', border: '1.5px solid #64748b', fontWeight: 700 }
                : { backgroundColor: '#f8fafc', color: '#64748b', border: '1px solid #cbd5e1', fontWeight: 600 }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedStatus(prev => (prev === 'draft' ? 'all' : 'draft'));
                setReportPage(1);
              }}
              title="임시저장 필터링"
            >
              {selectedStatus === 'draft' && <i className="fas fa-check me-1" style={{ fontSize: '0.68rem' }} />}
              임시저장 {countDraft}
            </button>
            <button
              type="button"
              className="badge wo-filter-badge-btn px-2 py-1"
              style={selectedStatus === 'request'
                ? { backgroundColor: '#ede9fe', color: '#5b21b6', border: '1.5px solid #8b5cf6', fontWeight: 700 }
                : { backgroundColor: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe', fontWeight: 600 }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedStatus(prev => (prev === 'request' ? 'all' : 'request'));
                setReportPage(1);
              }}
              title="접수대기 필터링"
            >
              {selectedStatus === 'request' && <i className="fas fa-check me-1" style={{ fontSize: '0.68rem' }} />}
              접수대기 {countRequest}
            </button>
            <button
              type="button"
              className="badge wo-filter-badge-btn px-2 py-1"
              style={selectedStatus === 'delay_request'
                ? { backgroundColor: '#fee2e2', color: '#991b1b', border: '1.5px solid #ef4444', fontWeight: 700 }
                : { backgroundColor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', fontWeight: 600 }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedStatus(prev => (prev === 'delay_request' ? 'all' : 'delay_request'));
                setReportPage(1);
              }}
              title="접수지연 필터링"
            >
              {selectedStatus === 'delay_request' && <i className="fas fa-check me-1" style={{ fontSize: '0.68rem' }} />}
              접수지연 {countDelayRequest}
            </button>
          </div>
        </div>

        {/* 타이트한 4열 고정 그리드 + 세로 스크롤 영역 (상하좌우 4px 패딩으로 테두리 잘림 방지) */}
        <div className="wo-assignee-scroll-area p-1">
          <div className="row g-2">
            {allAssignees.length === 0 ? (
              <div className="col-12 text-center text-muted py-3" style={{ fontSize: '0.8rem' }}>
                지정된 담당자가 없습니다.
              </div>
            ) : (
              allAssignees.map(a => (
                <div key={a.id} className="col-12 col-sm-6 col-md-4 col-lg-3">
                  <div
                    className="wo-assignee-stat-card py-1 px-2 rounded-2 h-100 d-flex flex-column justify-content-between"
                    style={{
                      border: selectedAssigneeId === a.id ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                      backgroundColor: selectedAssigneeId === a.id ? '#eff6ff' : '#f8fafc',
                      boxShadow: 'none',
                      minWidth: 0,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                    onClick={() => {
                      setSelectedAssigneeId(prev => (prev === a.id ? null : a.id));
                      setReportPage(1);
                    }}
                    title={`${a.name} ${a.pos} 처리결과 필터링 (클릭시 선택/해제)`}
                  >
                    <div style={{ minWidth: 0, overflow: 'hidden' }}>
                      <div className="d-flex justify-content-between align-items-center mb-0">
                        <span className={`text-truncate ${selectedAssigneeId === a.id ? 'fw-bold text-primary' : 'fw-bold text-dark'}`} style={{ fontSize: '0.78rem' }}>
                          {selectedAssigneeId === a.id && <i className="fas fa-check-circle me-1 text-primary" style={{ fontSize: '0.72rem' }} />}
                          {a.name} {a.pos}
                        </span>
                        <span
                          className="badge flex-shrink-0"
                          style={{
                            backgroundColor: a.badgeBg,
                            color: '#fff',
                            fontSize: '0.65rem',
                            padding: '3px 6px',
                            lineHeight: 1.2,
                            borderRadius: '4px'
                          }}
                        >
                          {a.statusText}
                        </span>
                      </div>
                      <div className="text-muted text-truncate" style={{ fontSize: '0.7rem' }}>{a.dept}</div>
                    </div>
                    <div
                      className="mt-1 pt-1 border-top d-flex align-items-center"
                      style={{ fontSize: '0.7rem', color: a.hasReport ? '#334155' : '#94a3b8', minWidth: 0, overflow: 'hidden' }}
                      title={a.note}
                    >
                      <i className={`fas ${a.hasReport ? 'fa-file-alt' : 'fa-minus-circle'} me-1 flex-shrink-0`} style={{ color: a.hasReport ? a.badgeBg : '#94a3b8', fontSize: '0.68rem' }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {a.note}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* ── 2. 중단: 결과보고서 목록 ── */}
      <div className="p-3 bg-white rounded-3 shadow-sm border d-flex flex-column flex-grow-1" style={{ borderColor: '#e2e8f0', minHeight: 0 }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="fw-bold m-0 d-flex align-items-center gap-2 flex-wrap" style={{ color: '#0d9488', fontSize: '1.05rem' }}>
            <i className="fas fa-file-signature fs-5" />
            <span>제출된 처리결과 목록</span>

            {/* 1. 선택된 담당자 조건 뱃지 (있을 때만 노출) */}
            {selectedAssignee && (
              <span
                className="badge d-inline-flex align-items-center gap-1.5 px-2 py-1 shadow-sm"
                style={{ backgroundColor: '#2563eb', color: '#fff', fontSize: '0.74rem', fontWeight: 700, borderRadius: '4px' }}
              >
                <i className="fas fa-user" style={{ fontSize: '0.68rem' }} />
                {selectedAssignee.name} {selectedAssignee.pos}
                <i
                  className="fas fa-times ms-1"
                  style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.25)', borderRadius: '50%', padding: '1px 3px', fontSize: '0.62rem' }}
                  onClick={(e) => { e.stopPropagation(); setSelectedAssigneeId(null); setReportPage(1); }}
                  title="담당자 필터 해제"
                />
              </span>
            )}

            {/* 2. 현재 상태 조건 뱃지 */}
            <span
              className="badge px-2 py-1 shadow-sm"
              style={{
                backgroundColor: STATUS_META[selectedStatus]?.badgeBg || '#1e293b',
                color: '#fff',
                fontSize: '0.74rem',
                fontWeight: 700,
                borderRadius: '4px'
              }}
            >
              {STATUS_META[selectedStatus]?.label || '전체'}
            </span>

            {/* 3. 총 건수 뱃지 */}
            <span className="badge rounded-pill ms-0.5" style={{ backgroundColor: '#0d9488', color: '#fff', fontSize: '0.74rem' }}>
              총 {filteredReports.length}건
            </span>
          </h6>

          {/* 우측 상단: 협조요청자 단독 1회 표출 */}
          {(() => {
            const name = workOrder?.author?.name || workOrder?.author_name || '요청자';
            const pos = workOrder?.author?.org?.position || workOrder?.author?.position || '';
            const dept = workOrder?.author?.org?.dept_name || workOrder?.author?.department || '';
            return (
              <div className="d-flex align-items-center ms-auto me-2 text-secondary" style={{ fontSize: '0.82rem' }}>
                <i className="fas fa-user-tie me-1.5" style={{ color: '#0d9488' }} />
                <span className="text-muted">협조요청자 : </span>
                <strong className="text-dark ms-1">{name}</strong>
                {pos && <span className="ms-1 fw-semibold text-secondary">{pos}</span>}
                {dept && <span className="ms-1 text-muted">({dept})</span>}
              </div>
            );
          })()}

          {/* 담당자 작성 / 이어쓰기 / 검토 대기 / 보완 대기 배지 (순차 결재 정책) */}
          {canWriteReport && (
            myDraftReport ? (
              <button
                type="button"
                className="btn btn-sm btn-warning text-dark fw-bold px-3 d-flex align-items-center gap-1 shadow-sm"
                style={{ fontSize: '0.82rem' }}
                onClick={() => onOpenReportForm(myDraftReport)}
                title="작성 중인 임시저장 처리결과를 이어서 작성합니다."
              >
                <i className="fas fa-edit" />
                <span>{nextSeqText}처리결과 이어쓰기 (임시저장)</span>
              </button>
            ) : isReviewPending ? (
              <div
                className="px-2.5 py-1.5 rounded-2 d-flex align-items-center gap-1.5 border shadow-sm"
                style={{ backgroundColor: '#fffbeb', borderColor: '#fde68a', color: '#b45309', fontSize: '0.78rem', fontWeight: 600 }}
                title="요청자의 처리결과 검토 및 확인을 기다리는 중입니다."
              >
                <i className="fas fa-clock text-warning" style={{ fontSize: '0.8rem' }} />
                <span>요청자가 {latestSubmittedReport?.report_seq || submittedCount}차 처리결과를 검토 중입니다.</span>
              </div>
            ) : isRejectedPending ? (
              <button
                type="button"
                className="btn btn-sm btn-danger fw-bold px-3 d-flex align-items-center gap-1 shadow-sm"
                style={{ fontSize: '0.82rem' }}
                onClick={() => onOpenReportForm(latestSubmittedReport)}
                title={`보완요청된 ${latestSubmittedReport?.report_seq || submittedCount}차 처리결과를 보완 수정합니다.`}
              >
                <i className="fas fa-redo-alt" />
                <span>{latestSubmittedReport?.report_seq || submittedCount}차 처리결과 보완하기</span>
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-sm btn-primary fw-bold px-3 d-flex align-items-center gap-1 shadow-sm"
                style={{ backgroundColor: '#0d9488', borderColor: '#0d9488', fontSize: '0.82rem' }}
                onClick={() => onOpenReportForm(null)}
                title={`${nextSeqText}처리결과를 작성합니다.`}
              >
                <i className="fas fa-plus-circle" />
                <span>{nextSeqText}처리결과 작성</span>
              </button>
            )
          )}

          {/* 담당자이나 아직 업무를 접수하지 않은 경우 안내 배지 */}
          {isWorkOrderActive && isAssigneeEligible && !isAssigneeAccepted && (
            <div
              className="px-2.5 py-1.5 rounded-2 d-flex align-items-center gap-1.5 text-muted border bg-light shadow-sm"
              style={{ fontSize: '0.78rem', fontWeight: 500 }}
              title="협조를 먼저 접수해야 처리결과를 작성할 수 있습니다."
            >
              <i className="fas fa-info-circle text-primary" style={{ fontSize: '0.8rem' }} />
              <span>협조 접수 시 처리결과를 작성할 수 있습니다.</span>
            </div>
          )}
        </div>

        {/* 보고서 카드 리스트 (4개 적정 높이 피팅 또는 0건 안내) */}
        <div className="d-flex flex-column gap-2 mb-2 flex-grow-1">
          {filteredReports.length === 0 ? (
            <div
              className="p-4 text-center bg-white rounded-3 border shadow-sm d-flex flex-column align-items-center justify-content-center flex-grow-1"
              style={{ borderColor: '#e2e8f0', minHeight: '260px', margin: '6px 0' }}
            >
              <div className="mb-3">
                <i className="fas fa-clipboard-check text-secondary" style={{ fontSize: '3.5rem', opacity: 0.35 }} />
              </div>
              <h6 className="fw-bold text-dark mb-2" style={{ fontSize: '1.05rem' }}>
                {selectedAssignee ? (
                  <span>
                    <strong className="text-dark">[{selectedAssignee.name} {selectedAssignee.pos}]</strong> 님의
                    <strong className="text-primary ms-1">[{STATUS_META[selectedStatus]?.label}]</strong> 처리결과가 없습니다.
                  </span>
                ) : (
                  <span>선택하신 상태(<strong className="text-primary">[{STATUS_META[selectedStatus]?.label}]</strong>)의 제출된 처리결과가 없습니다.</span>
                )}
              </h6>
              <p className="text-muted small mb-4" style={{ maxWidth: '480px' }}>
                선택하신 조건에 해당하는 처리결과가 아직 등록되지 않았습니다. 다른 담당자나 상태를 선택하시거나 전체 목록을 확인해보세요.
              </p>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary fw-bold px-3 py-2 shadow-sm"
                onClick={() => { setSelectedStatus('all'); setSelectedAssigneeId(null); setReportPage(1); }}
                style={{ fontSize: '0.82rem' }}
              >
                <i className="fas fa-undo me-1" />
                전체 처리결과 보기 (초기화)
              </button>
            </div>
          ) : (
            currentReports.map(rep => (
              <div
                key={rep.id}
                className="wo-report-card px-3 rounded-2 shadow-sm d-flex align-items-center justify-content-between"
                style={{ paddingTop: '12px', paddingBottom: '12px' }}
                onClick={() => {
                  if (onOpenReportDetail) {
                    onOpenReportDetail(rep.raw);
                  }
                }}
              >
                <div className="d-flex align-items-center gap-2 overflow-hidden flex-grow-1 me-3" style={{ minWidth: 0 }}>
                  <Avatar name={rep.authorName} size={32} />
                  <div className="overflow-hidden flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="d-flex align-items-center gap-1.5 mb-1 flex-nowrap" style={{ minWidth: 0 }}>
                      {Boolean(rep.raw?.is_final || Number(rep.raw?.progress_rate) === 100) ? (
                        <span className="badge bg-success flex-shrink-0" style={{ fontSize: '0.64rem', padding: '2px 6px', display: 'inline-flex', alignItems: 'center' }}>
                          최종결과
                        </span>
                      ) : (
                        <span className="badge bg-secondary flex-shrink-0" style={{ fontSize: '0.64rem', padding: '2px 6px', display: 'inline-flex', alignItems: 'center' }}>
                          {rep.seq || 1}차 결과
                        </span>
                      )}
                      <span className="fw-bold text-dark text-truncate" style={{ fontSize: '0.84rem' }} title={rep.title}>
                        {rep.title}
                      </span>
                      <span className="badge text-white flex-shrink-0 ms-1" style={{ backgroundColor: rep.badgeBg, fontSize: '0.64rem', padding: '2px 6px', display: 'inline-flex', alignItems: 'center' }}>
                        {rep.statusLabel}
                      </span>
                    </div>
                    <div className="text-secondary small d-flex align-items-center gap-3 flex-wrap" style={{ fontSize: '0.72rem' }}>
                      <span>작성자: <strong>{rep.author}</strong> ({rep.dept})</span>
                      <span>제출일시: {rep.date}</span>
                      {rep.files > 0 && (
                        <span><i className="fas fa-paperclip text-muted me-1" />산출물 {rep.files}건</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* 우측 정렬: 진척도 미니 게이지 바 및 수치 표출 (처리결과 화면과 100% 동일한 색상 및 줄무늬 적용) */}
                {(() => {
                  const rate = Math.min(100, Math.max(0, Number(rep.raw?.progress_rate || 0)));
                  const is100 = rate === 100;
                  const isApproved = ['approved', 'completed', 'done'].includes(rep.raw?.approval_status);
                  const activeColor = isApproved ? (is100 ? '#10b981' : '#2563eb') : '#6366f1';
                  const activeBg = isApproved ? (is100 ? '#ecfdf5' : '#eff6ff') : '#eef2ff';
                  const activeBorder = isApproved ? (is100 ? '#a7f3d0' : '#bfdbfe') : '#c7d2fe';

                  return (
                    <div className="d-flex align-items-center gap-2 ms-auto me-3 flex-shrink-0" style={{ minWidth: '200px' }}>
                      <span className="text-muted" style={{ fontSize: '0.74rem' }}>진척도</span>
                      <div className="progress flex-grow-1" style={{ height: '7px', backgroundColor: '#e2e8f0', borderRadius: '4px', minWidth: '110px', overflow: 'hidden' }}>
                        <div
                          className={`progress-bar ${!isApproved ? 'progress-bar-striped progress-bar-animated' : ''}`}
                          role="progressbar"
                          style={{
                            width: `${rate}%`,
                            backgroundColor: activeColor,
                            borderRadius: '4px'
                          }}
                        />
                      </div>
                      <div
                        className="d-flex align-items-center justify-content-center fw-bold rounded px-1.5"
                        style={{
                          fontSize: '0.76rem',
                          height: '22px',
                          backgroundColor: activeBg,
                          color: activeColor,
                          border: `1px solid ${activeBorder}`,
                          minWidth: '40px'
                        }}
                      >
                        {rate}%
                      </div>
                    </div>
                  );
                })()}

                <div className="d-flex align-items-center flex-shrink-0">
                  <span
                    className="btn btn-sm text-white fw-bold d-inline-flex align-items-center justify-content-center shadow-sm"
                    style={{
                      backgroundColor: rep.status === 'review' && (isAuthor || isAdmin) ? '#f59e0b' : '#0d9488',
                      borderColor: rep.status === 'review' && (isAuthor || isAdmin) ? '#f59e0b' : '#0d9488',
                      fontSize: '0.78rem',
                      borderRadius: '5px',
                      height: '31px',
                      padding: '0 12px',
                      gap: '5px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <span style={{ display: 'inline-block', lineHeight: 1 }}>{(isAuthor || isAdmin) && rep.status === 'review' ? '검토' : '열람'}</span>
                    <i className="fas fa-chevron-right" style={{ fontSize: '0.64rem', display: 'inline-block', lineHeight: 1 }} />
                  </span>
                </div>
              </div>
            )))}
        </div>

        {/* 페이징 UI & 페이지 알약 뱃지 (보고서가 있을 때만 노출) */}
        {filteredReports.length > 0 && (
          <div className="d-flex justify-content-between align-items-center pt-2 border-top">
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                padding: '3px 10px',
                borderRadius: '20px',
                fontSize: '0.78rem',
                color: '#1e3a8a',
                fontWeight: 600,
                boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
              }}
            >
              <span style={{ color: '#2563eb' }}>{reportPage}</span>
              <span style={{ color: '#93c5fd' }}>/</span>
              <span>{totalPages}</span>
              <span style={{ color: '#bfdbfe' }}>|</span>
              <span>총 {filteredReports.length}건</span>
            </div>

            {/* 독립형 라운드 페이징 버튼들 */}
            <div className="d-flex align-items-center" style={{ gap: '4px' }}>
              {/* 처음으로 */}
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={reportPage === 1}
                onClick={() => setReportPage(1)}
                style={{
                  minWidth: '28px',
                  height: '28px',
                  borderRadius: '4px',
                  padding: 0,
                  fontSize: '0.75rem',
                  opacity: reportPage === 1 ? 0.35 : 1,
                  cursor: reportPage === 1 ? 'default' : 'pointer'
                }}
                title="처음으로"
              >
                <i className="fas fa-angle-double-left" />
              </button>

              {/* 이전 */}
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={reportPage === 1}
                onClick={() => setReportPage(prev => Math.max(1, prev - 1))}
                style={{
                  minWidth: '28px',
                  height: '28px',
                  borderRadius: '4px',
                  padding: 0,
                  fontSize: '0.75rem',
                  opacity: reportPage === 1 ? 0.35 : 1,
                  cursor: reportPage === 1 ? 'default' : 'pointer'
                }}
                title="이전"
              >
                <i className="fas fa-chevron-left" />
              </button>

              {/* 페이지 번호 목록 */}
              {Array.from({ length: totalPages }).map((_, i) => {
                const p = i + 1;
                const isActive = reportPage === p;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setReportPage(p)}
                    style={{
                      minWidth: '28px',
                      height: '28px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: isActive ? '#4f46e5' : '#f1f5f9',
                      color: isActive ? '#ffffff' : '#475569',
                      fontWeight: isActive ? 700 : 500,
                      fontSize: '0.8rem',
                      boxShadow: isActive ? '0 2px 4px rgba(79, 70, 229, 0.3)' : 'none',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {p}
                  </button>
                );
              })}

              {/* 다음 */}
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={reportPage === totalPages}
                onClick={() => setReportPage(prev => Math.min(totalPages, prev + 1))}
                style={{
                  minWidth: '28px',
                  height: '28px',
                  borderRadius: '4px',
                  padding: 0,
                  fontSize: '0.75rem',
                  opacity: reportPage === totalPages ? 0.35 : 1,
                  cursor: reportPage === totalPages ? 'default' : 'pointer'
                }}
                title="다음"
              >
                <i className="fas fa-chevron-right" />
              </button>

              {/* 마지막으로 */}
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={reportPage === totalPages}
                onClick={() => setReportPage(totalPages)}
                style={{
                  minWidth: '28px',
                  height: '28px',
                  borderRadius: '4px',
                  padding: 0,
                  fontSize: '0.75rem',
                  opacity: reportPage === totalPages ? 0.35 : 1,
                  cursor: reportPage === totalPages ? 'default' : 'pointer'
                }}
                title="마지막으로"
              >
                <i className="fas fa-angle-double-right" />
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

export default WorkOrderReportsTab;
